import { FC } from 'react'
import gql from 'graphql-tag'
import { useRouter } from 'next/router'
import styled, { css } from 'styled-components'
import {
  CoverLetterItemScreen_SideBarContent_CoverLetterElasticEdgeFragment,
  CoverLetterItemScreen_SideBarContent_CurrentUserFragment,
} from 'generated/graphql'
import { getCoverLetterItemLinkProps } from 'screens/CoverLetterScreen/CoverLetterList/CoverLetterListContent'
import CoverLetterScrap from 'screens/CoverLetterScreen/CoverLetterScrap'
import LoadingListItem from 'screens/CoverLetterScreen/LoadingListItem'
import NotFound from 'screens/CoverLetterScreen/NotFound'
import CoverLetterListItemMoMui from 'screens/CoverLetterScreen/mobile/components/CoverLetterList/CoverLetterListItemMoMui'
import { useSearchStateContext } from 'screens/CoverLetterScreen/useSearchState'
import BookMark from 'svgIcons/BookMark'

interface SideBarContentProps {
  loading?: boolean
  coverLetterEdges: CoverLetterItemScreen_SideBarContent_CoverLetterElasticEdgeFragment[]
  currentUser?: CoverLetterItemScreen_SideBarContent_CurrentUserFragment | null
}

const SideBarContent: FC<SideBarContentProps> = ({
  loading = false,
  coverLetterEdges = [],
  currentUser,
}) => {
  const router = useRouter()
  const context = useSearchStateContext()

  if (loading) {
    return <LoadingListItem length={20} variant="mobile" />
  }

  const coverLetters = coverLetterEdges
  if (coverLetters?.length === 0) {
    return <NotFound />
  }

  return (
    <StyledWrapper>
      {coverLetters.map((edge) => {
        const coverLetter = edge.node

        const LinkProps = getCoverLetterItemLinkProps(context, coverLetter?.id)
        const isActive = router?.query?.id === coverLetter?.id

        if (!edge?.node?.id) {
          return null
        }

        return (
          <div
            key={edge?.node?.id}
            className={`item ${isActive ? 'active-item' : ''}`}
          >
            <CoverLetterListItemMoMui
              coverLetter={coverLetter}
              LinkProps={LinkProps}
              coverLetterHighlight={edge.highlight}
              classes={{
                specInfo: 'spec-info',
                content: 'content',
              }}
            />

            {/* 스크랩 버튼 */}
            <div className="scrap">
              <CoverLetterScrap
                coverLetter={coverLetter}
                currentUser={currentUser}
              >
                {({ scrapCoverLetter, loading, scrapCount }) => (
                  <>
                    <button
                      onClick={scrapCoverLetter}
                      className={`scrap-button ${
                        coverLetter?.isScrapped ? 'is-scrapped' : ''
                      }`}
                      disabled={loading}
                    >
                      <BookMark
                        isOn={!!coverLetter?.isScrapped}
                        className={coverLetter?.isScrapped ? 'is-scrapped' : ''}
                        strokeType="#666666"
                      />
                    </button>
                    <p
                      className={`scrap-count ${
                        coverLetter?.isScrapped ? 'is-scrapped' : ''
                      }`}
                    >
                      {(scrapCount || 0) >= 1000 ? '999+' : scrapCount}
                    </p>
                  </>
                )}
              </CoverLetterScrap>
            </div>
          </div>
        )
      })}
    </StyledWrapper>
  )
}

gql`
  fragment CoverLetterItemScreen_SideBarContent_currentUser on User {
    ...CoverLetterScrap_currentUser
  }
  fragment CoverLetterItemScreen_SideBarContent_coverLetterElasticEdge on CoverLetterElasticEdge {
    highlight
    node {
      id
      ...CoverLetterScrap_coverLetter
      ...CoverLetterListItemMobile_coverLetter
    }
  }
`

export default SideBarContent

const StyledWrapper = styled.div`
  ${({ theme }) => css`
    .item {
      width: 100%;
      display: flex;
      padding: 20px;
      border-bottom: 1px solid #eee;
      justify-content: space-between;
      align-items: center;

      @media (max-width: 600px) {
        background-color: ${theme.background.bg_8};
      }
    }

    .active-item {
      background-color: #f2faff;
      border: 1px solid ${theme.color.primary_2} !important;

      @media (max-width: 600px) {
        background-color: #f4f4f4;
        border: none;
      }
    }

    .scrap {
      width: 50px;
      text-align: center;
      margin-left: 20px;
      flex-shrink: 0;

      @media (max-width: 600px) {
        margin-left: 0;
      }
    }

    .is-scrapped {
      color: ${theme.color.primary_2} !important;
      font-weight: 500;
    }

    .scrap-count {
      font-size: 16px;
      font-weight: normal;
      color: #999;
    }

    .scrap-button {
      color: #e8e8e8;
      padding-bottom: 5px;
      background-color: transparent;
      border: none;
      padding: 12px 12px 0px;
      cursor: pointer;

      @media (hover: hover) and (pointer: fine) {
        &:hover {
          color: ${theme.color.primary_2};
        }
      }
    }

    .spec-info {
      @media (min-width: 960px) {
        font-size: 16px;
        color: #999;
      }
    }

    .content {
      @media (min-width: 960px) {
        font-size: 16px;
        color: ${theme.color.text_1};
      }
    }
  `}
`
